/**
 * DIALOGUES BLOQUANTS — spécification PARTAGÉE des canaux « confirmation » et « alerte » (§24.1quater A).
 *
 * Anatomie commune aux DEUX canaux (voile + carte + tuile d'icône + titre + message + boutons), en
 * DONNÉES : les rendus DOM (`ui-web/confirm|alert`) et React Native (`ui-native/confirm|alert`) lisent
 * ce fichier — une modification esthétique se fait ICI et s'applique aux deux plateformes.
 *
 * ⚠️ RÈGLES DE SENS (§24.1quater, non négociables — les rendus les APPLIQUENT, pas les appelants) :
 *   - CONFIRMATION : AVANT une action irréversible. Nomme la cible et l'ampleur ; le bouton d'action
 *     porte le VERBE (« Supprimer 3 lignes ») — JAMAIS « OK » ni « Oui » → `confirmLabel` est REQUIS.
 *     Ordre CONSTANT : Annuler à gauche, action à droite. Échap = annuler, Entrée = confirmer,
 *     focus initial sur le bouton d'action, clic sur le voile = annuler.
 *   - ALERTE : une action a ÉCHOUÉ (message RÉEL de l'échec, §23.0) ou accusé de réception bloquant.
 *     Un seul bouton (« Fermer » par défaut). Échap/Entrée/voile = fermer.
 *   - Les COULEURS ne sont PAS ici (chaque plateforme a son thème) : seuls leurs RÔLES sont nommés
 *     (`DIALOG_COLOR_ROLES`), comme pour le toast.
 */

/** Tons d'une confirmation : destructif (défaut) ou neutre/informatif. */
export type ConfirmTone = 'danger' | 'info';
/** Tons d'une alerte : échec (défaut), consigne à ACQUITTER (`warn` — pas un échec : un
 *  avertissement d'administration, une échéance qui approche), ou accusé de réception. */
export type AlertTone = 'err' | 'warn' | 'info';

/** Nom d'icône par ton — le paquet impose le NOM, chaque app fournit le dessin (registre injecté). */
import type { IconName } from './icons';

export const CONFIRM_ICON: Record<ConfirmTone, IconName> = { danger: 'alert', info: 'info' };
export const ALERT_ICON: Record<AlertTone, IconName> = { err: 'alert', warn: 'alert', info: 'info' };

/**
 * Carte (géométrie commune confirm/alert) : posée au CENTRE de l'écran, son contenu aligné au DÉBUT.
 * ⚠️ Pastille, titre et message partent du bord gauche, les boutons se rangent à droite (décision
 * utilisateur sur la maquette du store) : on lit une question de gauche à droite et on répond en bas
 * à droite, là où les fenêtres des systèmes posent l'action.
 * ⚠️ Le rendu React Native (tablette) centre encore son contenu : l'aligner passe par un APK.
 */
export const DIALOG_CARD = {
  width: 420,
  /** Une confirmation qui liste ses changements : l'ancienne et la nouvelle valeur tiennent sur une ligne. */
  listWidth: 480,
  radius: 16,
  padding: 24,
  gap: 14,
  align: 'start',
  titleSize: 16,
  titleWeight: 700,
  messageSize: 13.5,
  messageLineHeight: 1.55,
} as const;

/** Tuile d'icône ronde en tête de carte. */
export const DIALOG_TILE = { size: 52, radius: 999, iconSize: 24, iconStroke: 2 } as const;

/** Rangée de boutons (alignée à droite ; Annuler PUIS action — ordre constant). */
export const DIALOG_BUTTONS = { height: 38, radius: 9, padH: 16, gap: 10, marginTop: 6, justify: 'end', fontSize: 13.5, fontWeight: 600 } as const;

/** Voile : assombrit et floute légèrement le fond (l'app reste visible mais inerte). */
export const DIALOG_OVERLAY = { opacity: 0.5, blur: 3 } as const;

/** Entrée : fondu + léger zoom, comme le toast. */
export const DIALOG_TIMING = { enterMs: 180, enterScaleFrom: 0.96 } as const;

/** Défauts de canal — ce que les rendus appliquent quand l'appelant ne précise pas. */
export const CONFIRM_DEFAULTS = { cancelLabel: 'Annuler', tone: 'danger' as ConfirmTone } as const;
export const ALERT_DEFAULTS = { closeLabel: 'Fermer', tone: 'err' as AlertTone } as const;

/**
 * Rôles de couleur à résoudre par le thème local — le SEUL point à vérifier à la main lors d'un
 * changement de charte (mêmes conventions que `TOAST_COLOR_ROLES`).
 */
export const DIALOG_COLOR_ROLES = {
  overlay: "voile sombre semi-transparent (rôle ink-950 à ~50 %)",
  card: "fond de carte (rôle paper) et texte (rôle ink-900 titre / ink-600 message)",
  tileDanger: "tuile danger/échec : fond rose doux (rose-bg), icône rose soutenu (rose)",
  tileInfo: "tuile info : fond teal doux (teal-100), icône teal soutenu (teal-700)",
  cancel: "bouton Annuler : neutre (fond ink-100, texte ink-800)",
  actionDanger: "bouton d'action destructif : fond danger, texte blanc",
  actionPrimary: "bouton d'action non destructif : fond teal-600, texte blanc",
} as const;
